import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Snowflake, MapPin, Package, Plus, LogOut } from 'lucide-react';
import EntradaDeEmbrioes from './EntradaDeEmbrioes';
import SaidaDeEmbrioes from './SaidaDeEmbrioes';
import SobrasDescongeladas from './SobrasDescongeladas';
import {
  listarCongelados, definirLocal, agruparPorLocal, agruparPorCliente,
  resumoDoEstoque, faixaDeNumeros, enderecoDoBotijao,
  juntarEmHaste, separarDaHaste, comAHasteInteira, hastesPorTamanho, textoDasHastes,
  ordenaPorHaste,
} from '../../../data/estoque';
import { MARK } from '../../../constants';

const input = 'px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const Numero = ({ rotulo, valor, detalhe, alerta }) => (
  <div>
    <p className="text-slate-500 text-xs">{rotulo}</p>
    <p className={`text-lg font-semibold ${alerta ? 'text-slate-900' : 'text-slate-900'}`}>
      {valor}{alerta ? ` ${MARK.WARN}` : ''}
    </p>
    {detalhe && <p className="text-xs text-slate-400">{detalhe}</p>}
  </div>
);

/**
 * O que está dentro do botijão agora.
 *
 * A tela existe porque o dado existia e ninguém conseguia olhar: hoje há 25
 * embriões congelados e nenhum com localização — o campo vivia escondido numa
 * tabela de lançamento da FIV, sem nenhuma tela que respondesse "onde está o
 * que eu tenho".
 *
 * Por isso a ação principal aqui é ATRIBUIR local, em lote. Ninguém guarda uma
 * palheta por vez; guarda a caneca inteira de um cliente.
 */
const EstoqueDeCongelados = ({ laboratoryId, clients = [], podeMexer = true }) => {
  const [linhas, setLinhas]   = useState([]);
  const [carregando, setCar]  = useState(true);
  const [erro, setErro]       = useState('');
  const [visao, setVisao]     = useState('local');   // 'local' | 'cliente'
  const [sel, setSel]         = useState(new Set());
  const [botijao, setBotijao] = useState('');
  const [caneca, setCaneca]   = useState('');
  const [rack, setRack]       = useState('');
  const [altura, setAltura]   = useState('');
  const [data, setData]       = useState('');
  const [haste, setHaste]     = useState('');
  const [salvando, setSalv]   = useState(false);
  const [dandoEntrada, setEntrada] = useState(false);
  const [dandoSaida, setSaida]     = useState(false);

  const carregar = useCallback(async () => {
    if (!laboratoryId) return;
    setCar(true); setErro('');
    try {
      setLinhas(await listarCongelados(laboratoryId) || []);
      setSel(new Set());
    } catch (e) {
      console.error('estoque de congelados:', e);
      setErro(e.message);
    } finally { setCar(false); }
  }, [laboratoryId]);

  useEffect(() => { carregar(); }, [carregar]);

  const resumo = useMemo(() => resumoDoEstoque(linhas), [linhas]);
  const grupos = useMemo(
    () => (visao === 'local' ? agruparPorLocal(linhas) : agruparPorCliente(linhas)),
    [linhas, visao]);

  // A haste sai inteira: marcar ou desmarcar um embrião dela vale para todos.
  const alternar = (id) => setSel(s => {
    const daHaste = comAHasteInteira(new Set([id]), linhas);
    const n = new Set(s);
    const tirar = n.has(id);
    daHaste.forEach(x => (tirar ? n.delete(x) : n.add(x)));
    return n;
  });

  const alternarGrupo = (itens) => setSel(s => {
    const n = new Set(s);
    const todos = itens.every(i => n.has(i.embryo_id));
    itens.forEach(i => (todos ? n.delete(i.embryo_id) : n.add(i.embryo_id)));
    return comAHasteInteira(n, linhas);
  });

  const selecionadas = linhas.filter(l => sel.has(l.embryo_id));
  const podeHaste = selecionadas.length > 0 && selecionadas.every(l => l.metodo === 'Vitrificação');
  const temHaste = selecionadas.some(l => l.haste);

  const emHaste = async (separar) => {
    setSalv(true); setErro('');
    try {
      if (separar) await separarDaHaste([...sel]);
      else await juntarEmHaste([...sel], haste || null);
      setHaste('');
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally { setSalv(false); }
  };

  const aplicar = async () => {
    if (sel.size === 0 || !botijao.trim()) return;
    setSalv(true); setErro('');
    try {
      await definirLocal([...sel], { botijao, caneca, rack, altura, freeze_date: data || null });
      setBotijao(''); setCaneca(''); setRack(''); setAltura(''); setData('');
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally { setSalv(false); }
  };

  if (carregando) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg px-6 py-10 text-center">
        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600 mx-auto mb-3" />
        <p className="text-slate-600 text-sm">Abrindo o botijão…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Os números ── */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <Numero rotulo="Congelados" valor={resumo.total}
            detalhe={`${resumo.vitrificados} vitri · ${resumo.dt} DT`} />
          {/* Número próprio, e não diluído no total: é a única métrica desta
              tela que pede ação. */}
          <Numero rotulo="Sem localização" valor={resumo.semLocal}
            alerta={resumo.semLocal > 0}
            detalhe={resumo.semLocal > 0 ? 'ninguém acha' : 'tudo localizado'} />
          <Numero rotulo="Botijões" valor={resumo.botijoes} />
          <Numero rotulo="Clientes" valor={resumo.clientes} />
        </div>

        {podeMexer && (
        <div className="mt-3 flex justify-end">
          <button onClick={() => setEntrada(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
            <Plus size={14} /> Dar entrada
          </button>
        </div>
        )}

        {resumo.semLocal > 0 && (
          <p className="mt-3 text-xs text-slate-600">
            {MARK.WARN} {resumo.semLocal} de {resumo.total} embriões congelados não têm
            botijão informado. Selecione abaixo e informe onde estão — é o que
            transforma este estoque em algo que dá para ir buscar.
          </p>
        )}
      </div>

      <SobrasDescongeladas laboratoryId={laboratoryId} podeMexer={podeMexer} />

      {/* ── Ação em lote ── */}
      {/* Some inteira para quem só olha: caixa de seleção e botão que não
          levam a nada são pior que ausência — a pessoa tenta, não acontece
          nada, e ela acha que o sistema travou. */}
      {podeMexer && (
      <div className="bg-white border border-slate-200 rounded-lg p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex items-center gap-2 mr-2">
            <MapPin size={16} className="text-slate-400" />
            <span className="text-sm font-medium text-slate-800">
              {sel.size === 0 ? 'Selecione embriões' : `${sel.size} selecionado${sel.size > 1 ? 's' : ''}`}
            </span>
          </div>
          <input className={`${input} w-32`} placeholder="Botijão *" value={botijao}
            onChange={(e) => setBotijao(e.target.value)} />
          <input className={`${input} w-24`} placeholder="Caneca" value={caneca}
            onChange={(e) => setCaneca(e.target.value)} />
          <input className={`${input} w-24`} placeholder="Rack" value={rack}
            onChange={(e) => setRack(e.target.value)} />
          <select className={`${input} w-28`} value={altura}
            onChange={(e) => setAltura(e.target.value)}>
            <option value="">Altura…</option>
            <option value="cima">Cima</option>
            <option value="baixo">Baixo</option>
          </select>
          <input className={`${input} w-40`} type="date" value={data}
            title="Data de congelamento (opcional)"
            onChange={(e) => setData(e.target.value)} />
          <button onClick={aplicar} disabled={sel.size === 0 || !botijao.trim() || salvando}
            className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-40">
            {salvando ? 'Gravando…' : 'Definir local'}
          </button>
          {/* Haste: só vitrificado. Número em branco = haste nova, com o
              próximo número do laboratório; número informado = entra nela. */}
          {podeHaste && (
            <>
              <input className={`${input} w-24`} type="number" min="1" placeholder="Haste nº"
                title="Em branco: haste nova, próximo número. Informado: entra nessa haste."
                value={haste} onChange={(e) => setHaste(e.target.value)} />
              <button onClick={() => emHaste(false)} disabled={salvando}
                className="px-3 py-1.5 border border-slate-300 rounded text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                Juntar em haste
              </button>
              {temHaste && (
                <button onClick={() => emHaste(true)} disabled={salvando}
                  className="px-3 py-1.5 border border-slate-300 rounded text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                  Separar da haste
                </button>
              )}
            </>
          )}
          {sel.size > 0 && (
            <button onClick={() => setSaida(true)}
              className="px-3 py-1.5 border border-slate-300 rounded text-sm text-slate-700 hover:bg-slate-50">
              Dar saída
            </button>
          )}
          {sel.size > 0 && (
            <button onClick={() => setSel(new Set())}
              className="px-3 py-1.5 border border-slate-300 rounded text-sm text-slate-700 hover:bg-slate-50">
              Limpar seleção
            </button>
          )}

          <div className="ml-auto flex rounded border border-slate-300 overflow-hidden text-sm">
            {[['local', 'Por botijão'], ['cliente', 'Por cliente']].map(([v, r]) => (
              <button key={v} onClick={() => setVisao(v)}
                className={`px-3 py-1.5 ${visao === v ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'}`}>
                {r}
              </button>
            ))}
          </div>
        </div>

        {erro && (
          <div className="mt-2 bg-slate-50 border border-slate-300 text-slate-700 text-sm rounded px-3 py-2">
            {erro}
          </div>
        )}
      </div>
      )}

      {/* ── Os grupos ── */}
      {linhas.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg px-6 py-10 text-center">
          <Snowflake className="mx-auto mb-3 text-slate-400" size={40} />
          <p className="text-slate-600 text-sm">Nenhum embrião congelado.</p>
          <p className="text-slate-400 text-xs mt-1">
            Embrião entra aqui quando a FIV registra destino congelado.
          </p>
        </div>
      ) : grupos.map((g, gi) => {
        const titulo = visao === 'local'
          ? (enderecoDoBotijao(g) || 'Sem localização')
          : g.client_name;
        const semLocal = visao === 'local' && !g.botijao;
        const todosSel = g.itens.every(i => sel.has(i.embryo_id));
        // Por cliente, a resposta a "tenho haste com três?".
        const hastes = visao === 'cliente' ? textoDasHastes(hastesPorTamanho(g.itens)) : '';
        const itens = ordenaPorHaste(g.itens);

        return (
          <div key={`${titulo}-${gi}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
              {podeMexer && (
                <input type="checkbox" checked={todosSel}
                  onChange={() => alternarGrupo(g.itens)}
                  className="rounded border-slate-300" />
              )}
              <Package size={14} className="text-slate-400" />
              <span className="font-medium text-slate-900 text-sm">{titulo}</span>
              {semLocal && <span className="text-xs">{MARK.WARN}</span>}
              <span className="text-xs text-slate-500">
                {g.itens.length} {g.itens.length > 1 ? 'embriões' : 'embrião'}
                {/* Só quando há número. O embrião congelado aqui não entra na
                    numeração do serviço — ela é dos que vão a campo —, e a
                    faixa vazia deixava um "nº " solto na etiqueta da caneca. */}
                {faixaDeNumeros(g.itens.map(i => i.numero)) &&
                  ` · nº ${faixaDeNumeros(g.itens.map(i => i.numero))}`}
              </span>
              {hastes && <span className="text-xs text-slate-500">· VT: {hastes}</span>}
            </div>

            <div className="overflow-x-auto">
              {/* Cada grupo é uma tabela. Com largura automática, cada uma media
                  as colunas pelo próprio conteúdo, e elas mudavam de lugar de um
                  grupo para o outro. Largura fixa alinha todas; as colunas sem
                  largura (Cliente ou Onde está, Doadora e Touro) dividem o resto
                  por igual. */}
              <table className="w-full min-w-[62rem] table-fixed text-sm break-words">
                <colgroup>
                  <col className="w-8" />
                  <col className="w-14" />
                  <col className="w-20" />
                  <col />
                  <col />
                  <col />
                  <col className="w-20" />
                  <col className="w-32" />
                  <col className="w-32" />
                </colgroup>
                <thead className="text-slate-500 border-b border-slate-100">
                  <tr>
                    <th></th>
                    <th className="px-2 py-1.5 text-left font-medium">Nº</th>
                    <th className="px-2 py-1.5 text-left font-medium">Haste</th>
                    {visao === 'local' && <th className="px-2 py-1.5 text-left font-medium">Cliente</th>}
                    {visao === 'cliente' && <th className="px-2 py-1.5 text-left font-medium">Onde está</th>}
                    <th className="px-2 py-1.5 text-left font-medium">Doadora</th>
                    <th className="px-2 py-1.5 text-left font-medium">Touro</th>
                    <th className="px-2 py-1.5 text-center font-medium">Fase</th>
                    <th className="px-2 py-1.5 text-left font-medium">Método</th>
                    <th className="px-2 py-1.5 text-left font-medium">Serviço</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {itens.map(i => (
                    <tr key={i.embryo_id} className={sel.has(i.embryo_id) ? 'bg-blue-50' : 'hover:bg-slate-50'}>
                      <td className="pl-4">
                        {podeMexer && (
                          <input type="checkbox" checked={sel.has(i.embryo_id)}
                            onChange={() => alternar(i.embryo_id)}
                            className="rounded border-slate-300" />
                        )}
                      </td>
                      <td className="px-2 py-1.5 font-medium text-slate-900">{i.numero ?? '—'}</td>
                      <td className="px-2 py-1.5 text-slate-700">
                        {i.haste
                          ? <>H{i.haste} <span className="text-xs text-slate-400">({linhas.filter(l => l.haste === i.haste).length})</span></>
                          : '—'}
                      </td>
                      {visao === 'local' && (
                        <td className="px-2 py-1.5 text-slate-700">{i.client_name || '—'}</td>
                      )}
                      {visao === 'cliente' && (
                        <td className="px-2 py-1.5 text-slate-700">
                          {i.botijao
                            ? enderecoDoBotijao(i)
                            : <span className="text-slate-400">{MARK.WARN} sem local</span>}
                          {/* Texto antigo, quando houver: é dado real e some da
                              vista se a tela só olhar os campos novos. */}
                          {!i.botijao && i.local_legado && (
                            <span className="text-xs text-slate-400 block">anotado: {i.local_legado}</span>
                          )}
                        </td>
                      )}
                      <td className="px-2 py-1.5 text-slate-700">{i.doadora || '—'}</td>
                      <td className="px-2 py-1.5 text-slate-700">{i.touro || '—'}</td>
                      <td className="px-2 py-1.5 text-center text-slate-600">{i.stage || '—'}</td>
                      <td className="px-2 py-1.5 text-slate-600">{i.metodo || '—'}</td>
                      <td className="px-2 py-1.5 text-slate-500 text-xs">
                        {i.servico_numero ? `#${i.servico_numero}` : '—'}
                        {i.servico_data && ` · ${new Date(`${i.servico_data}T00:00:00`).toLocaleDateString('pt-BR')}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {dandoSaida && (
        <SaidaDeEmbrioes
          selecionados={linhas.filter(l => sel.has(l.embryo_id))}
          onPronto={carregar}
          onFechar={() => setSaida(false)}
        />
      )}

      {dandoEntrada && (
        <EntradaDeEmbrioes
          laboratoryId={laboratoryId}
          clients={clients}
          onPronto={carregar}
          onFechar={() => setEntrada(false)}
        />
      )}
    </div>
  );
};

export default EstoqueDeCongelados;
